import { useState, useRef, useEffect } from "react";
import Modal from "../../../UI/Modal";
import {
  SubmitBtn,
  CancelBtn,
  QtyModBtn,
  InputField,
} from "../../../UI/FormElements";
import { swapTicketSchema } from "../../../../utils/schemas";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { numToCurrency } from "../../../../utils/currencyHelper";
import { STATUS } from "../../../../utils/constants";
import {
  setShowKeyboard,
  setKeyboardConfiguration,
  swapTicket,
  clearOrder,
} from "../../../../redux/slices";
import { useKeyboard } from "../../../../context/KeyboardContext";
import { useDispatch, useSelector } from "react-redux";
import SwapTicketReprint from "./SwapTicketReprint";
import { useReactToPrint } from "react-to-print";
import PaymentChannels from "../../../orderModule/paymentChannelsModal";

// Rounds to 2dp so split channel amounts don't drift from float arithmetic.
const round2 = (n) => Math.round(n * 100) / 100;

const unitPrice = (line) => +line.priceInPackage || +line.price;

const lineAmount = (line) => +line.orderQuantity * unitPrice(line);

const lineLabel = (line, quantity) =>
  line?.rowName && line?.seatNumber
    ? `${line.rowName}${line.seatNumber}`
    : `${line?.name} x${quantity}`;

const buildNewTicketSale = (line, quantity) => ({
  quantity,
  voucherCode: line.voucherCode || null,
  packageTicketId: line.packageTicketId || null,
  isLoyaltyTicket: false,
  amount: quantity * unitPrice(line),
  showtimeId: line.showtimeId,
  ticketId: line.ticketId,
  priceCardTicketId: line.priceCardTicketId,
  ticketPrice: line.priceInPackage || line.price,
  seatNumber: line?.seatNumber,
  seatLayoutId: line?.seatLayoutId,
  rowName: line?.rowName,
});

// Pairs the selected original ticket lines with the replacement cart lines,
// ticket by ticket. Each pair becomes one SwapTicket call, since the API swaps
// one original line for one replacement line at a time. e.g. 4 original lines
// of 1 seat each against 4 seated cart lines gives 4 pairs of quantity 1.
const pairTickets = (selectedSales, cartLines) => {
  const olds = selectedSales.map((x) => ({ ...x, remaining: x.quantity }));
  const news = cartLines.map((line) => ({
    line,
    remaining: +line.orderQuantity,
  }));
  const pairs = [];
  let o = 0;
  let n = 0;
  while (o < olds.length && n < news.length) {
    const quantity = Math.min(olds[o].remaining, news[n].remaining);
    if (quantity > 0) {
      pairs.push({
        ticketSale: olds[o].ticketSale,
        line: news[n].line,
        quantity,
        diff:
          quantity *
          (unitPrice(news[n].line) - +olds[o].ticketSale.ticketPrice),
      });
    }
    olds[o].remaining -= quantity;
    news[n].remaining -= quantity;
    if (olds[o].remaining === 0) o++;
    if (news[n].remaining === 0) n++;
  }
  return pairs;
};

// Splits the entered channels across swap calls so that each call receives
// exactly the amount it needs. Channels are consumed in order and may be split
// between calls; extra fields (giftCardId, memberId, otherChannelId) are kept.
const allocateChannels = (channels, needs) => {
  const pool = channels.map((c) => ({ ...c, amount: +c.amount }));
  return needs.map((need) => {
    let remaining = round2(need);
    const allocated = [];
    for (const channel of pool) {
      if (remaining <= 0) break;
      if (channel.amount <= 0) continue;
      const take = round2(Math.min(channel.amount, remaining));
      allocated.push({ ...channel, amount: take });
      channel.amount = round2(channel.amount - take);
      remaining = round2(remaining - take);
    }
    return allocated;
  });
};

const toErrorText = (err) =>
  typeof err === "string" ? err : err?.message || "Unable to swap ticket";

const TicketList = ({
  openStatus,
  closeFunc,
  orderData,
  closeParent,
  isOnlineBooking = false,
}) => {
  const [showChannelsModal, setShowChannelsModal] = useState(false);
  // One entry per cart ticket line: { label, line, payload, done }.
  // Kept after a partial failure so Proceed retries only the unswapped seats.
  const [swapPlan, setSwapPlan] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [printItems, setPrintItems] = useState(null);
  const { control, watch, reset, handleSubmit, setValue } = useForm({
    resolver: yupResolver(swapTicketSchema),
    defaultValues: {},
  });
  const [errMsg, setErrMsg] = useState("");
  const { t } = useTranslation(["Admin", "Common"]);
  const { setSetSharedInput } = useKeyboard();
  const dispatch = useDispatch();
  const { orderItems, customChannels, swapStatus } = useSelector(
    (state) => state?.orders
  );
  const printRef = useRef();

  const planInProgress = swapPlan.some((step) => step.done);

  const resetSwapState = () => {
    setSwapPlan([]);
    setIsRunning(false);
    setPrintItems(null);
  };

  const updatedCloseFunc = () => {
    dispatch(clearOrder());
    resetSwapState();
    closeFunc();
    closeParent();
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      updatedCloseFunc();
    },
  });

  // Print once the reprint component has rendered the swapped seats.
  useEffect(() => {
    if (printItems) {
      handlePrint();
    }
    // handlePrint is recreated every render; only printItems should trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printItems]);

  const finishSwap = (swappedLines) => {
    if (!isOnlineBooking && swappedLines.length > 0) {
      setPrintItems(swappedLines);
    } else {
      updatedCloseFunc();
    }
  };

  const newTickets = orderItems?.filter((item) => {
    return item?.showtimeId;
  });

  const { fields } = useFieldArray({
    control,
    name: "refundPaymentChannels",
  });

  // Original ticket lines chosen for the swap: { [ticketSaleId]: quantity }.
  const [selections, setSelections] = useState({});

  // Fresh form every time the list opens for an order.
  useEffect(() => {
    if (!openStatus) return;
    const channels = orderData?.paymentChannels?.map((channel) => {
      return {
        channelId: channel?.channelId,
        amount: 0,
        channel: channel?.channel,
        maxAmount: channel?.amount,
      };
    });
    reset({ reason: "", refundPaymentChannels: channels });
    setSelections({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openStatus, orderData]);

  const formData = watch();

  const selectedSales = (orderData?.ticketSales || [])
    .filter((sale) => selections[sale.id] > 0)
    .map((sale) => ({ ticketSale: sale, quantity: selections[sale.id] }));
  const hasSelection = selectedSales.length > 0;

  const toggleSale = (sale) => {
    if (planInProgress) return;
    setSelections((prev) => {
      const next = { ...prev };
      if (next[sale.id]) {
        delete next[sale.id];
      } else {
        next[sale.id] = sale.quantity;
      }
      return next;
    });
  };

  const changeSaleQuantity = (sale, delta) => {
    setSelections((prev) => {
      const next = { ...prev };
      const quantity = Math.min(
        Math.max((next[sale.id] || 0) + delta, 0),
        sale.quantity
      );
      if (quantity === 0) {
        delete next[sale.id];
      } else {
        next[sale.id] = quantity;
      }
      return next;
    });
  };

  const selectedQuantity = selectedSales.reduce(
    (sum, x) => sum + x.quantity,
    0
  );
  const oldTicketSum = selectedSales.reduce(
    (sum, x) => sum + x.quantity * +x.ticketSale.ticketPrice,
    0
  );
  const newTicketSum = newTickets?.reduce((sum, line) => sum + lineAmount(line), 0);
  const newTicketQuantity = newTickets?.reduce(
    (sum, line) => sum + +line.orderQuantity,
    0
  );

  // Runs the pending steps one after another. Each step is a separate
  // SwapTicket call, so a failure part-way leaves earlier seats swapped.
  const runPlan = async (plan) => {
    setIsRunning(true);
    setErrMsg("");
    const updatedPlan = [...plan];

    for (let i = 0; i < updatedPlan.length; i++) {
      if (updatedPlan[i].done) continue;
      try {
        await dispatch(swapTicket(updatedPlan[i].payload)).unwrap();
        updatedPlan[i] = { ...updatedPlan[i], done: true };
        setSwapPlan([...updatedPlan]);
      } catch (err) {
        const swapped = updatedPlan.filter((step) => step.done);
        const pending = updatedPlan.filter((step) => !step.done);
        setSwapPlan([...updatedPlan]);
        setIsRunning(false);
        setErrMsg(
          `${
            swapped.length
              ? `Swapped: ${swapped.map((s) => s.label).join(", ")}. `
              : ""
          }Failed on ${updatedPlan[i].label}: ${toErrorText(err)}. ` +
            `Press Proceed to retry ${pending
              .map((s) => s.label)
              .join(", ")}${
              swapped.length ? ", or Cancel to print the swapped seats" : ""
            }.`
        );
        return;
      }
    }

    setIsRunning(false);
    finishSwap(updatedPlan.map((step) => step.printItem));
  };

  // Builds one SwapTicket payload per original/replacement pair, with the
  // refund or extra payment channels split so each call carries its own
  // difference.
  const buildPlan = (reason, { refundChannels = [], paymentChannels = [] }) => {
    const pairs = pairTickets(selectedSales, newTickets);
    const refunds = allocateChannels(
      refundChannels,
      pairs.map((p) => (p.diff < 0 ? -p.diff : 0))
    );
    const payments = allocateChannels(
      paymentChannels,
      pairs.map((p) => (p.diff > 0 ? p.diff : 0))
    );

    return pairs.map((pair, i) => ({
      label: lineLabel(pair.line, pair.quantity),
      // Only this pair's share of the cart line gets printed.
      printItem: { ...pair.line, orderQuantity: pair.quantity },
      done: false,
      payload: {
        reason: reason || "",
        ticketSaleId: pair.ticketSale.id,
        quantity: pair.quantity,
        newTicketSale: buildNewTicketSale(pair.line, pair.quantity),
        paymentChannels: payments[i],
        refundPaymentChannels: refunds[i],
        //   source: "source-d9c05d",
      },
    }));
  };

  const submitForm = (data) => {
    if (isRunning) return;

    // Retry after a partial failure: keep the original plan and only run the
    // seats that haven't been swapped yet.
    if (swapPlan.length > 0 && swapPlan.some((step) => !step.done)) {
      runPlan(swapPlan);
      return;
    }

    //this helps to only select channels through which an amount has been entered for a refund
    const formattedRefundChannels = (data?.refundPaymentChannels || [])
      .filter((x) => {
        return x?.amount > 0;
      })
      .map((x) => {
        //this if check helps to format for cases where its a custom channel..Similar to order creation,
        //it makes the channelId the "others" channelId and puts the actual channelId as otherChannelId
        if (
          customChannels?.find((i) => {
            return i.id === x?.channelId;
          })
        ) {
          return {
            channelId: "channel-2d1980",
            otherChannelId: x?.channelId,
            amount: x?.amount,
          };
        } else {
          return { channelId: x?.channelId, amount: x?.amount };
        }
      });

    //calculating what the total value spread across the refund channels is
    const formattedRefundChannelsTotal = round2(
      formattedRefundChannels.reduce((a, b) => a + +b.amount, 0)
    );
    //check for quantity of ticket to be swapped
    if (selectedQuantity === 0) {
      setErrMsg("Select the ticket(s) to be swapped");
      return;
    }
    //check for when quantity of replacement is not equal to new quantity in cart
    if (newTicketQuantity !== selectedQuantity) {
      setErrMsg(
        `Ensure Quantity of tickets to be swapped (${selectedQuantity}) is equal to quantity of replacement tickets in the cart (${newTicketQuantity})`
      );
      return;
    }
    //each pair is swapped in its own call, so a mix of dearer and cheaper
    //replacements can't be settled as a single net refund or payment
    const diffs = pairTickets(selectedSales, newTickets).map((p) => p.diff);
    if (diffs.some((d) => d > 0) && diffs.some((d) => d < 0)) {
      setErrMsg(
        "Replacement tickets must all cost more, or all cost less, than the original. Swap them separately"
      );
      return;
    }
    //check for when refund channels does not add up to refunded amount
    if (
      oldTicketSum > newTicketSum &&
      formattedRefundChannelsTotal !== round2(oldTicketSum - newTicketSum)
    ) {
      setErrMsg(
        "Ensure the total Amount to be refunded is captured in the Refund Information channels"
      );
      return;
    }

    setErrMsg("");

    //This caters for when the swap requires additional payment
    //It captures the channels for additional payment then does the swap flow
    if (newTicketSum > oldTicketSum) {
      setShowChannelsModal(true);
      return;
    }

    //Same price or refund: swap each seat, print and reset the cart
    const plan = buildPlan(data.reason, {
      refundChannels: formattedRefundChannels,
    });
    setSwapPlan(plan);
    runPlan(plan);
  };

  const channelsModalCallBack = (balancePaymentChannels = []) => {
    // Rebuild the plan now that the extra payment channels are known.
    const plan = buildPlan(formData?.reason, {
      paymentChannels: balancePaymentChannels,
    });
    setSwapPlan(plan);
    runPlan(plan);
  };

  const handleCancel = () => {
    // Some seats already went through: print those so the customer isn't left
    // without tickets, then close.
    if (planInProgress && !isRunning) {
      finishSwap(swapPlan.filter((step) => step.done).map((step) => step.line));
      setErrMsg("");
      return;
    }
    closeFunc();
    setErrMsg("");
    resetSwapState();
    setSelections({});
    reset({});
  };

  return (
    <Modal
      openStatus={openStatus}
      closeFunc={closeFunc}
      panelStyles="bg-white p-[20px] min-h-[250px] min-w-[550px]"
    >
      <p className="font-bold text-center py-2">
        {!hasSelection
          ? "Select Ticket(s)"
          : `Selected ${selectedQuantity} of ${newTicketQuantity} ticket(s) in cart. Enter other information where applicable`}
      </p>
      <form onSubmit={handleSubmit(submitForm)}>
        <div className="max-h-[40vh] overflow-y-auto">
          {orderData?.ticketSales?.map((data) => {
            const selectedQty = selections[data.id] || 0;
            const isSelected = selectedQty > 0;
            return (
              <div
                key={data.id}
                className={`flex justify-between border-2 rounded p-2 mb-2 ${
                  isSelected ? "border-blue-700 bg-blue-50" : ""
                } ${planInProgress ? "" : "cursor-pointer"}`}
                onClick={() => toggleSale(data)}
              >
                <div>
                  <div className="flex">
                    <p className="mr-2 font-bold truncate max-w-[250px]">
                      {data?.film}
                    </p>
                    <span>{data?.screen}</span>
                  </div>
                  <p>
                    <span className="mr-2">
                      {new Date(data?.startTime).toDateString()}
                    </span>
                    <span>
                      {new Date(data?.startTime).toLocaleTimeString()}
                    </span>
                  </p>
                  <p className="text-[12px] text-gray-600">
                    {data?.rowName && data?.seatNumber
                      ? `Seat ${data.rowName}${data.seatNumber} · `
                      : ""}
                    Qty {data?.quantity} · {numToCurrency(data?.ticketPrice)}
                  </p>
                </div>
                {isSelected && !planInProgress && (
                  <div
                    className="flex items-center justify-between"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <QtyModBtn
                      btnTxt={"-"}
                      clickFunc={() => changeSaleQuantity(data, -1)}
                    />
                    <span className="mx-3">{selectedQty}</span>
                    <QtyModBtn
                      btnTxt={"+"}
                      clickFunc={() => changeSaleQuantity(data, 1)}
                    />
                  </div>
                )}
                {isSelected && planInProgress && (
                  <span className="self-center font-bold">{selectedQty}</span>
                )}
              </div>
            );
          })}
        </div>
        {hasSelection && (
          <>
            {oldTicketSum > newTicketSum && (
              <div className="border-2 rounded p-2">
                <p className="text-center bold">Refund Information</p>
                <p>
                  Amount to Refund: {numToCurrency(oldTicketSum - newTicketSum)}
                </p>
                {formData.refundPaymentChannels && (
                  <div className=" max-h-[60vh] overflow-y-auto border-t-[1px] border-[#7E208080]">
                    <div className="flex items-center justify-between px-2 py-1 border-b-[1px] border-[#7E208080]">
                      <span className=" text-[#C96FCC] font-bold">
                        {t("modals.order.refund.partial.columns.a")}
                      </span>{" "}
                      <span className=" text-[#C96FCC] font-bold">
                        {t("modals.order.refund.partial.columns.b")}
                      </span>
                      <span className=" text-[#C96FCC] font-bold">
                        {t("modals.order.refund.partial.columns.c")}
                      </span>
                    </div>

                    {fields?.map((field, index) => {
                      return (
                        <div
                          className="flex items-center justify-between px-2 py-1 border-b-[1px] border-[#7E208080]"
                          key={index}
                        >
                          <p>{field.channel}</p>
                          <p>{numToCurrency(field.maxAmount)}</p>
                          <InputField
                            inputType={"number"}
                            value={watch(
                              `refundPaymentChannels.${index}.amount`
                            )}
                            keyboardType="numeric"
                            setValue={(val) =>
                              setValue(
                                `refundPaymentChannels.${index}.amount`,
                                val
                              )
                            }
                            max={field?.maxAmount}
                            min={0}
                            disabled={planInProgress}
                            className="max-w-[80px] p-2 rounded bg-app-purple-10 border-2"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="mb-2">
              <label htmlFor={"reason"} className="block mb-2">
                {" "}
                {t("modals.order.refund.label")}
              </label>
              <textarea
                id={"reason"}
                className="w-full border-2 p-2 rounded bg-app-purple-10"
                rows={4}
                placeholder={"Enter reason for Swap"}
                value={watch(`reason`)}
                onClick={() => {
                  dispatch(setShowKeyboard(true));
                  dispatch(
                    setKeyboardConfiguration({
                      type: "alphaNumeric",
                      sharedInput: watch(`reason`),
                      placeHolder: "Enter reason for Swap",
                    })
                  );
                  setSetSharedInput(() => (val) => setValue(`reason`, val));
                }}
              ></textarea>
            </div>
            {newTicketSum > oldTicketSum && (
              <p>
                Amount to Balance: {numToCurrency(newTicketSum - oldTicketSum)}
              </p>
            )}
          </>
        )}
        {errMsg && (
          <div className="flex justify-center">
            {" "}
            <p className="text-red-500 text-center w-[400px]">{errMsg}</p>
          </div>
        )}
        <div className="flex justify-center my-3">
          {hasSelection && (
            <SubmitBtn
              btnText={planInProgress ? "Retry remaining" : "Proceed"}
              type={"submit"}
              status={isRunning ? STATUS.PENDING : swapStatus}
            />
          )}
          <CancelBtn btnFunc={handleCancel} />
        </div>
      </form>
      <div className="hidden">
        <SwapTicketReprint
          ref={printRef}
          currentOrderId={orderData?.id}
          items={printItems || undefined}
        />
      </div>
      {showChannelsModal && (
        <PaymentChannels
          openStatus={showChannelsModal}
          closeFunc={() => {
            setShowChannelsModal(false);
          }}
          swapTicketUseCase={true}
          swapTicketPaymentTotal={newTicketSum - oldTicketSum}
          swapTicketCallBackFunc={channelsModalCallBack}
        />
      )}
    </Modal>
  );
};
export default TicketList;
